import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Users, 
  Phone, 
  Briefcase, 
  AlertCircle, 
  Award, 
  Calendar,
  Plus, 
  Trash2,
  Upload,
  Clock,
  Loader2
} from "lucide-react";
import { formatPhoneNumber } from "@/lib/format";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

// Staff roles available (must match database CHECK constraint)
const staffRoles = [
  { value: 'instructor', label: 'Instructor' },
  { value: 'admin', label: 'Administrator' },
  { value: 'billing', label: 'Billing' },
  { value: 'front_desk', label: 'Front Desk' }
];

// Days of the week for availability
const daysOfWeek = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

interface EmergencyContact {
  id?: string;
  name: string;
  relationship: string;
  phone: string;
  is_primary: boolean;
}

interface Certification {
  id?: string;
  name: string;
  issuer: string;
  expiry_date?: string;
  certification_number?: string;
}

interface Availability {
  id?: string;
  day_of_week: string;
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface StaffFormData {
  first_name: string | undefined;
  last_name: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  role: string | undefined;
  
  // Employment details
  start_date: string | undefined;
  location_id: string | undefined;
  is_active: boolean | undefined;
  
  // Personal information
  birthday_month: number | undefined;
  birthday_day: number | undefined;
  profile_photo_url: string | undefined;
  
  // Address information
  address_line1: string | undefined;
  address_line2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zip: string | undefined;
  
  // Staff-specific data
  emergency_contacts: EmergencyContact[] | undefined;
  certifications: Certification[] | undefined;
  availability: Availability[] | undefined;
  notes: string | undefined;
}

interface StaffFormFieldsProps {
  formData: StaffFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSwitchChange?: (name: string, checked: boolean) => void;
  handleImageUpload?: (file: File) => Promise<void>;
}

const StaffFormFields: React.FC<StaffFormFieldsProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  handleImageUpload,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(formData.profile_photo_url || null);
  const [formattedPhone, setFormattedPhone] = useState(formData.phone || '');
  const [isUploading, setIsUploading] = useState(false);
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>(
    formData.emergency_contacts || [{name: '', relationship: '', phone: '', is_primary: true}]
  );
  const [certifications, setCertifications] = useState<Certification[]>(
    formData.certifications || [{name: '', issuer: '', expiry_date: '', certification_number: ''}]
  );
  const [availability, setAvailability] = useState<Availability[]>(
    formData.availability || daysOfWeek.map(day => ({
      day_of_week: day.value,
      start_time: '09:00',
      end_time: '17:00',
      is_available: false
    }))
  );

  // Format phone number when the component mounts or formData.phone changes
  useEffect(() => {
    if (formData.phone) {
      setFormattedPhone(formatPhoneNumber(formData.phone));
    }
  }, [formData.phone]);

  // Initialize emergency contacts from props
  useEffect(() => {
    if (formData.emergency_contacts && formData.emergency_contacts.length > 0) {
      setEmergencyContacts(formData.emergency_contacts);
    }
  }, [formData.emergency_contacts]);

  // Update preview URL when profile_photo_url changes
  useEffect(() => {
    if (formData.profile_photo_url) {
      setPreviewUrl(formData.profile_photo_url);
    }
  }, [formData.profile_photo_url]);

  const onImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    
    try {
      // Use the provided handleImageUpload function if available
      if (handleImageUpload) {
        await handleImageUpload(file);
        // The handleImageUpload from useStaffForm will handle the form data update and auto-save
        return;
      }
      
      // Fallback to direct upload if no handleImageUpload provided
      const fileExt = file.name.split('.').pop();
      const fileName = `staff-${Math.random()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('staff-photos')
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('staff-photos')
        .getPublicUrl(data.path);

      // Set preview URL from the uploaded file
      setPreviewUrl(publicUrl);

      // Update form data with the new URL
      if (handleChange) {
        const syntheticEvent = {
          target: { name: 'profile_photo_url', value: publicUrl }
        } as React.ChangeEvent<HTMLInputElement>;
        handleChange(syntheticEvent);
      }

      toast.success('Photo uploaded successfully!');
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setIsUploading(false);
    }
  };

  const onSwitchChange = (name: string, checked: boolean) => {
    if (handleSwitchChange) {
      handleSwitchChange(name, checked);
    }
  };

  // Handle phone input with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setFormattedPhone(formatted);
    
    // Create a synthetic event to pass to handleChange
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'phone',
        value: formatted
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    handleChange(syntheticEvent);
  };

  // Emergency contact handlers
  const addEmergencyContact = () => {
    const newContacts = [...emergencyContacts, {
      name: '',
      relationship: '',
      phone: '',
      is_primary: false
    }];
    setEmergencyContacts(newContacts);
  };

  const removeEmergencyContact = (index: number) => {
    if (emergencyContacts.length <= 1) return;
    
    const newContacts = emergencyContacts.filter((_, i) => i !== index);
    
    // If we removed the primary contact and there are other contacts left,
    // make the first one primary
    if (emergencyContacts[index].is_primary && newContacts.length > 0) {
      newContacts[0].is_primary = true;
    }
    
    setEmergencyContacts(newContacts);
  };

  const updateEmergencyContact = (index: number, field: keyof EmergencyContact, value: any) => {
    const updatedContacts = [...emergencyContacts];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    
    // If setting as primary, update other contacts to not be primary
    if (field === 'is_primary' && value === true) {
      updatedContacts.forEach((contact, i) => {
        if (i !== index) {
          contact.is_primary = false;
        }
      });
    }
    
    setEmergencyContacts(updatedContacts);
  };

  // Certification handlers
  const addCertification = () => {
    const newCertifications = [...certifications, {
      name: '',
      issuer: '',
      expiry_date: '',
      certification_number: ''
    }];
    setCertifications(newCertifications);
  };

  const removeCertification = (index: number) => {
    if (certifications.length <= 1) return;
    setCertifications(certifications.filter((_, i) => i !== index));
  };

  const updateCertification = (index: number, field: keyof Certification, value: string) => {
    const updatedCertifications = [...certifications];
    updatedCertifications[index] = { ...updatedCertifications[index], [field]: value };
    setCertifications(updatedCertifications);
  };

  // Availability handlers
  const updateAvailability = (index: number, field: keyof Availability, value: any) => {
    const updatedAvailability = [...availability];
    updatedAvailability[index] = { ...updatedAvailability[index], [field]: value };
    setAvailability(updatedAvailability);
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || 'S';
    const last = lastName?.charAt(0) || 'M';
    return `${first}${last}`.toUpperCase();
  };

  return (
    <div className="space-y-8">
      {/* Personal Information */}
      <Card className="border border-blue-200 bg-blue-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-blue-900">Personal Information</h3>
          </div>
          
          <div className="grid gap-6">
            {/* Profile Photo */}
            <div className="flex items-center gap-6">
              <div className="relative">
                {previewUrl ? (
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={previewUrl} alt="Staff photo" />
                    <AvatarFallback className="bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700 text-lg">
                      {getInitials(formData.first_name, formData.last_name)}
                    </AvatarFallback>
                    {isUploading && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      </div>
                    )}
                  </Avatar>
                ) : (
                  <Avatar className="h-20 w-20">
                    <AvatarFallback className="bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700 text-lg">
                      {getInitials(formData.first_name, formData.last_name)}
                    </AvatarFallback>
                    {isUploading && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      </div>
                    )}
                  </Avatar>
                )}
              </div>
              
              <div className="flex-1">
                <Label htmlFor="profile_photo" className="text-sm font-medium">Profile Photo</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="profile_photo"
                    name="profile_photo"
                    type="file"
                    accept="image/*"
                    onChange={onImageChange}
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => document.getElementById('profile_photo')?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Photo
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Name and Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name || ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name || ""}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formattedPhone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                />
              </div>
            </div>

            {/* Birthday (Month and Day only for HR) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="birthday_month">Birth Month</Label>
                <select
                  id="birthday_month"
                  name="birthday_month"
                  value={formData.birthday_month || ""}
                  onChange={(e) => {
                    const syntheticEvent = {
                      target: { name: 'birthday_month', value: e.target.value ? parseInt(e.target.value) : undefined }
                    } as React.ChangeEvent<HTMLInputElement>;
                    handleChange(syntheticEvent);
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select Month</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="birthday_day">Birth Day</Label>
                <select
                  id="birthday_day"
                  name="birthday_day"
                  value={formData.birthday_day || ""}
                  onChange={(e) => {
                    const syntheticEvent = {
                      target: { name: 'birthday_day', value: e.target.value ? parseInt(e.target.value) : undefined }
                    } as React.ChangeEvent<HTMLInputElement>;
                    handleChange(syntheticEvent);
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select Day</option>
                  {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* Employment Details */}
      <Card className="border border-purple-200 bg-purple-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-purple-900">Employment Details</h3>
          </div>
          
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={formData.role || ""}
                  onValueChange={(value) => {
                    const syntheticEvent = {
                      target: { name: 'role', value }
                    } as React.ChangeEvent<HTMLInputElement>;
                    handleChange(syntheticEvent);
                  }}
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffRoles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <DatePicker
                  value={formData.start_date ? new Date(formData.start_date) : undefined}
                  onChange={(date) => {
                    const syntheticEvent = {
                      target: {
                        name: "start_date",
                        value: date ? date.toISOString().split('T')[0] : ""
                      }
                    } as React.ChangeEvent<HTMLInputElement>;
                    handleChange(syntheticEvent);
                  }}
                  placeholder="Select start date"
                />
              </div>
            </div>
            
            {/* Active status */}
            <div className="flex items-center justify-between space-x-2 border border-purple-200 p-3 rounded-md bg-purple-50">
              <div>
                <Label htmlFor="is_active" className="text-base">Active Employee</Label>
                <p className="text-sm text-muted-foreground">Staff member is currently active</p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active !== false}
                onCheckedChange={(checked) => onSwitchChange('is_active', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contacts */}
      <Card className="border border-destructive/20 bg-destructive/10">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-destructive/20 rounded-full flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-destructive">Emergency Contacts</h3>
          </div>
          
          <div className="grid gap-4">
            {emergencyContacts.map((contact, index) => (
              <div key={index} className="border border-destructive/20 bg-white rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-destructive">Emergency Contact {index + 1}</h4>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeEmergencyContact(index)}
                    disabled={emergencyContacts.length <= 1}
                    className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                
                <div className="grid gap-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor={`ec-name-${index}`}>Name</Label>
                      <Input
                        id={`ec-name-${index}`}
                        value={contact.name}
                        onChange={(e) => updateEmergencyContact(index, 'name', e.target.value)}
                        placeholder="Full Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`ec-relationship-${index}`}>Relationship</Label>
                      <Input
                        id={`ec-relationship-${index}`}
                        value={contact.relationship}
                        onChange={(e) => updateEmergencyContact(index, 'relationship', e.target.value)}
                        placeholder="Spouse, Parent, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`ec-phone-${index}`}>Phone</Label>
                      <Input
                        id={`ec-phone-${index}`}
                        value={contact.phone}
                        onChange={(e) => updateEmergencyContact(index, 'phone', formatPhoneNumber(e.target.value))}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between space-x-2 border border-destructive/20 p-2 rounded-md bg-destructive/10">
                    <div>
                      <Label htmlFor={`ec-primary-${index}`} className="text-sm">Primary Contact</Label>
                      <p className="text-xs text-muted-foreground">Contact first in emergency</p>
                    </div>
                    <Switch
                      id={`ec-primary-${index}`}
                      checked={contact.is_primary}
                      onCheckedChange={(checked) => updateEmergencyContact(index, 'is_primary', checked)}
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              className="mt-2 border-destructive/20 text-destructive hover:bg-destructive/10"
              onClick={addEmergencyContact}
            >
              <Plus size={16} className="mr-2" />
              Add Emergency Contact
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Certifications & Skills */}
      <Card className="border border-orange-200 bg-orange-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <Award className="h-4 w-4 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-orange-900">Certifications & Skills</h3>
          </div>
          
          <div className="grid gap-4">
            {certifications.map((cert, index) => (
              <div key={index} className="border border-orange-300 bg-white rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-orange-800">Certification {index + 1}</h4>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeCertification(index)}
                    disabled={certifications.length <= 1}
                    className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                
                <div className="grid gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor={`cert-name-${index}`}>Certification Name</Label>
                      <Input
                        id={`cert-name-${index}`}
                        value={cert.name}
                        onChange={(e) => updateCertification(index, 'name', e.target.value)}
                        placeholder="e.g., CPR Certification"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`cert-issuer-${index}`}>Issuing Organization</Label>
                      <Input
                        id={`cert-issuer-${index}`}
                        value={cert.issuer}
                        onChange={(e) => updateCertification(index, 'issuer', e.target.value)}
                        placeholder="e.g., American Red Cross"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor={`cert-number-${index}`}>Certification Number</Label>
                      <Input
                        id={`cert-number-${index}`}
                        value={cert.certification_number || ''}
                        onChange={(e) => updateCertification(index, 'certification_number', e.target.value)}
                        placeholder="Certificate ID"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`cert-expiry-${index}`}>Expiry Date</Label>
                      <Input
                        id={`cert-expiry-${index}`}
                        type="date"
                        value={cert.expiry_date || ''}
                        onChange={(e) => updateCertification(index, 'expiry_date', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              className="mt-2 border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={addCertification}
            >
              <Plus size={16} className="mr-2" />
              Add Certification
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Schedule & Availability */}
      <Card className="border border-green-200 bg-green-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Clock className="h-4 w-4 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-900">Schedule & Availability</h3>
          </div>
          
          <div className="grid gap-3">
            {availability.map((avail, index) => (
              <div key={index} className="border border-green-300 bg-white rounded-lg p-3">
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`avail-${index}`}
                      checked={avail.is_available}
                      onCheckedChange={(checked) => updateAvailability(index, 'is_available', checked)}
                    />
                    <Label htmlFor={`avail-${index}`} className="text-sm font-medium w-20">
                      {daysOfWeek.find(d => d.value === avail.day_of_week)?.label}
                    </Label>
                  </div>
                  
                  {avail.is_available && (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={avail.start_time}
                        onChange={(e) => updateAvailability(index, 'start_time', e.target.value)}
                        className="w-32"
                      />
                      <span className="text-sm text-muted-foreground">to</span>
                      <Input
                        type="time"
                        value={avail.end_time}
                        onChange={(e) => updateAvailability(index, 'end_time', e.target.value)}
                        className="w-32"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Card className="border border-gray-200 bg-gray-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
          </div>
          
          <div className="grid gap-4">
            {/* Address */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-700">Home Address</h4>
              <div className="grid gap-3">
                <div className="space-y-2">
                  <Label htmlFor="address_line1">Address Line 1</Label>
                  <Input
                    id="address_line1"
                    name="address_line1"
                    value={formData.address_line1 || ""}
                    onChange={handleChange}
                    placeholder="123 Main St"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    name="address_line2"
                    value={formData.address_line2 || ""}
                    onChange={handleChange}
                    placeholder="Apt, Suite, etc."
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city || ""}
                      onChange={handleChange}
                      placeholder="City"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state || ""}
                      onChange={handleChange}
                      placeholder="State"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP Code</Label>
                    <Input
                      id="zip"
                      name="zip"
                      value={formData.zip || ""}
                      onChange={handleChange}
                      placeholder="12345"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows={3}
                placeholder="Any additional information about this staff member..."
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffFormFields;